import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, deleteDoc, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styled from 'styled-components';
import { FaTrash, FaEnvelope, FaPhone } from 'react-icons/fa';

// Premium color palette
const COLORS = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  dark: '#0f172a',
  light: '#f8fafc',
  white: '#ffffff',
  gray: '#64748b',
  lightGray: '#f1f5f9',
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
};

// Premium styled components
const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// const MessageItem = styled.div`
//   background: ${COLORS.white};
//   border-radius: 1rem;
//   padding: 1.5rem;
//   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
//   border: 1px solid ${COLORS.lightGray};
//   transition: all 0.2s ease;
  
//   &:hover {
//     transform: translateY(-2px);
//     box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
//   }
// `;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

// const MessageInfo = styled.div`
//   flex: 1;
// `;

const MessageName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${COLORS.primary};
  margin-bottom: 0.5rem;
`;

const MessageContact = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${COLORS.gray};
  font-size: 0.875rem;
`;

const MessageDate = styled.div`
  color: ${COLORS.gray};
  font-size: 0.75rem;
  margin-bottom: 1rem;
`;

const MessageContent = styled.div`
  color: ${COLORS.primary};
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const DeleteButton = styled.button`
  background: ${COLORS.danger};
  color: ${COLORS.white};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc2626;
    transform: scale(1.05);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${COLORS.gray};
  background: ${COLORS.white};
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
`;

const MessageCard = styled.div`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

// const MessagesGrid = styled.div`
//   display: grid;
//   grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
//   gap: 1.5rem;
// `;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${COLORS.lightGray};
  background-color: ${props => props.active ? COLORS.primary : COLORS.white};
  color: ${props => props.active ? COLORS.white : COLORS.primary};
  cursor: pointer;
  
  &:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  
  &:last-child {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  
  &:disabled {
    background-color: ${COLORS.lightGray};
    color: ${COLORS.gray};
    cursor: not-allowed;
  }
`;

const ConfirmModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: ${COLORS.primary};
`;

const ModalText = styled.p`
  color: ${COLORS.gray};
  margin-bottom: 1.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${COLORS.lightGray};
  color: ${COLORS.primary};
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const ConfirmButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${COLORS.danger};
  color: ${COLORS.white};
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  
  &:hover {
    background-color: #b91c1c;
  }
`;

const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const messagesPerPage = 6;

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const messagesCollection = collection(db, 'contactMessages');
        const messagesQuery = query(messagesCollection, orderBy('createdAt', 'desc'));
        const messagesSnapshot = await getDocs(messagesQuery);
        
        const messagesData = messagesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().createdAt ? new Date(doc.data().createdAt) : null
        }));
        
        setMessages(messagesData);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
  }, []);

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteDoc(doc(db, 'contactMessages', messageId));
      setMessages(prevMessages => prevMessages.filter(message => message.id !== messageId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle both Firestore timestamps and ISO string dates
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = timestamp;
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pagination
  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = messages.slice(indexOfFirstMessage, indexOfLastMessage);
  const totalPages = Math.ceil(messages.length / messagesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      {loading ? (
        <div>Loading messages...</div>
      ) : messages.length > 0 ? (
        <>
          <MessagesList>
            {currentMessages.map(message => (
              <MessageCard key={message.id}>
                <MessageHeader>
                  <MessageName>{message.name}</MessageName>
                  <MessageDate>{formatDate(message.timestamp)}</MessageDate>
                </MessageHeader>
                
                <MessageContact>
                  <ContactItem>
                    <FaEnvelope /> {message.email}
                  </ContactItem>
                  {message.phone && (
                    <ContactItem>
                      <FaPhone /> {message.phone}
                    </ContactItem>
                  )}
                </MessageContact>
                
                <MessageContent>{message.message}</MessageContent>
                
                <DeleteButton onClick={() => setDeleteConfirm(message)}>
                  <FaTrash /> Delete
                </DeleteButton>
              </MessageCard>
            ))}
          </MessagesList>
          
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </PaginationButton>
              
              {[...Array(totalPages).keys()].map(number => (
                <PaginationButton
                  key={number + 1}
                  active={currentPage === number + 1}
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </PaginationButton>
              ))}
              
              <PaginationButton
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </PaginationButton>
            </PaginationContainer>
          )}
        </>
      ) : (
        <EmptyState>
          <h3>No messages found</h3>
          <p>When users submit contact forms, they will appear here.</p>
        </EmptyState>
      )}
      
      {deleteConfirm && (
        <ConfirmModal>
          <ModalContent>
            <ModalTitle>Delete Message</ModalTitle>
            <ModalText>
              Are you sure you want to delete this message from {deleteConfirm.name}? This action cannot be undone.
            </ModalText>
            <ModalActions>
              <CancelButton onClick={() => setDeleteConfirm(null)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={() => handleDeleteMessage(deleteConfirm.id)}>
                Delete
              </ConfirmButton>
            </ModalActions>
          </ModalContent>
        </ConfirmModal>
      )}
    </>
  );
};

export default MessagesManagement;
